import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Copy, Edit2, Eye, Trash2, CheckCircle } from "react-feather";
import Breadcrumb from "../../common/breadcrumb";
import { C, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { Badge, Loader, RCard, SectionTitle } from "../../reporting/ReportingShared";
import { primaryBtnStyle, secondaryBtnStyle, withDisabled, hintStyle } from "../../common/FormKit";
import { successToast } from "../../../shared/Toast";
import {
  activatePosReceiptTemplate, deletePosReceiptTemplate, duplicatePosReceiptTemplate, listPosReceiptTemplates,
} from "../../../utils/posReceiptTemplateApi";
import { SYSTEM_TEMPLATE_HINTS } from "./posReceiptTemplateMeta";
import useSelectedMerchant from "./useSelectedMerchant";

/**
 * POS receipt templates — which layout a merchant's POS prints the customer receipt with (Epson).
 *
 * Two lists: the system templates every merchant can use or clone (Compact = today's receipt,
 * Large = the bigger look), and the merchant's own. "Set active" moves the merchant's pointer;
 * whether the POS actually uses it is the admin-only switch on Edit Merchant, shown as a badge here.
 */

const iconBtn = (disabled) => withDisabled({ ...secondaryBtnStyle, padding: "6px 10px", fontSize: 12 }, disabled);

const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "—");

const PosReceiptTemplatesPage = () => {
  const navigate = useNavigate();
  const { merchantId, hasMerchant, isAdmin, readOnly } = useSelectedMerchant();

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    if (!hasMerchant) return;
    setLoading(true);
    const d = await listPosReceiptTemplates(merchantId);
    setData(d);
    setLoading(false);
  }, [hasMerchant, merchantId]);

  useEffect(() => { load(); }, [load]);

  const clone = async (t) => {
    setBusy(true);
    const res = await duplicatePosReceiptTemplate(t.id, { merchantId });
    setBusy(false);
    if (res?.data?.id) {
      successToast("Template cloned — you are editing the copy");
      navigate(`/merchant/pos-receipt-templates/${res.data.id}`);
    }
  };

  const activate = async (t) => {
    setBusy(true);
    const res = await activatePosReceiptTemplate(t.id, merchantId);
    setBusy(false);
    if (res) {
      successToast(res.message || "Template is now active");
      await load();
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    const res = await deletePosReceiptTemplate(confirmDelete.id);
    setBusy(false);
    setConfirmDelete(null);
    if (res) {
      successToast("Template deleted");
      await load();
    }
  };

  const activeId = data?.activeTemplateId ?? null;
  const enabled = !!data?.enabled;

  const row = (t, { system }) => {
    const active = t.id === activeId || (activeId === null && system && t.key === "compact");
    const canEdit = !readOnly && (system ? isAdmin : true);
    return (
      <div
        key={t.id}
        data-testid={`template-${t.id}`}
        style={{
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "12px 14px", borderRadius: 10,
          border: `1px solid ${active ? C.coral : C.borderLight}`, background: active ? C.coralLight : C.bg, marginBottom: 10,
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{t.name}</span>
            {active && <Badge color={C.coral} bg={C.white}>Active</Badge>}
            {system && <Badge color={C.textMed} bg={C.white}>System</Badge>}
          </div>
          <div style={{ fontSize: 12, color: C.textLight, marginTop: 2 }}>
            {system ? (SYSTEM_TEMPLATE_HINTS[t.key] || "System template") : `Your template · updated ${fmtDate(t.updatedAt)}`}
          </div>
        </div>
        <button type="button" style={iconBtn(busy)} disabled={busy} onClick={() => navigate(`/merchant/pos-receipt-templates/${t.id}`)}>
          {canEdit ? <><Edit2 size={12} /> Edit</> : <><Eye size={12} /> Preview</>}
        </button>
        {!readOnly && (
          <button type="button" style={iconBtn(busy)} disabled={busy} onClick={() => clone(t)}>
            <Copy size={12} /> Clone
          </button>
        )}
        {!readOnly && !active && (
          <button type="button" style={iconBtn(busy)} disabled={busy} onClick={() => activate(t)} aria-label={`Set ${t.name} active`}>
            <CheckCircle size={12} /> Set active
          </button>
        )}
        {!readOnly && !system && (
          <button type="button" style={iconBtn(busy || active)} disabled={busy || active} title={active ? "Set another template active first" : "Delete"} onClick={() => setConfirmDelete(t)} aria-label={`Delete ${t.name}`}>
            <Trash2 size={12} />
          </button>
        )}
      </div>
    );
  };

  const body = () => {
    if (!hasMerchant) {
      return <RCard><p style={{ textAlign: "center", padding: 40, color: C.textLight, margin: 0 }}>Select a merchant</p></RCard>;
    }
    if (loading || !data) return <RCard><Loader /></RCard>;
    return (
      <>
        <RCard>
          <SectionTitle>System templates</SectionTitle>
          <p style={{ ...hintStyle, marginTop: 0, marginBottom: 12 }}>
            Ready-made layouts. {isAdmin ? "You can edit them for everyone, or clone one into this merchant." : "Clone one to make your own version."}
          </p>
          {data.system.map((t) => row(t, { system: true }))}
        </RCard>
        <div style={{ height: 16 }} />
        <RCard>
          <SectionTitle>{data.merchant?.name ? `${data.merchant.name}'s templates` : "My templates"}</SectionTitle>
          {data.custom.length === 0 ? (
            <p style={{ ...hintStyle, marginTop: 0 }}>No custom templates yet. Clone a system template to start.</p>
          ) : (
            data.custom.map((t) => row(t, { system: false }))
          )}
        </RCard>
      </>
    );
  };

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="POS Receipt Templates" parent="Merchants" />
      <Container fluid className="rpt-wrap">
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>POS customer receipt</div>
            <div style={{ fontSize: 12, color: C.textLight }}>
              How this merchant&apos;s POS prints the tax invoice on Epson printers. One active template for every device.
            </div>
          </div>
          <div style={{ flex: 1 }} />
          {hasMerchant && data && (
            enabled
              ? <Badge color={C.green} bg={C.greenLight}>Enabled on the POS</Badge>
              : <Badge color={C.orange} bg={C.orangeLight}>Off — the POS prints its built-in receipt{isAdmin ? " (turn on in Edit Merchant → Printing)" : " (ask an admin to turn it on)"}</Badge>
          )}
          <Badge color={C.textMed} bg={C.bg}>Sunmi printers: coming soon</Badge>
        </div>

        {body()}
      </Container>

      <Modal isOpen={!!confirmDelete} toggle={() => setConfirmDelete(null)} centered>
        <ModalHeader toggle={() => setConfirmDelete(null)}>Delete template</ModalHeader>
        <ModalBody>Delete <strong>{confirmDelete?.name}</strong>? This cannot be undone.</ModalBody>
        <ModalFooter>
          <button type="button" style={secondaryBtnStyle} onClick={() => setConfirmDelete(null)}>Cancel</button>
          <button type="button" style={withDisabled({ ...primaryBtnStyle, background: "#dc2626", boxShadow: "none" }, busy)} disabled={busy} onClick={remove}>Delete</button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default PosReceiptTemplatesPage;
